import { useEffect, useState } from "react";
import ProductVariantPicker from "./ProductVariantPicker";
import { ProductPriceBlock } from "./ProductPriceBlock";
import { BtnContent } from "./BtnIcon";
import {
  IoAddOutline,
  IoCartOutline,
  IoCloseOutline,
  IoRemoveOutline,
} from "react-icons/io5";

function ShopVariantModal({ entry, selectedVariant, onSelect, onConfirm, onClose }) {
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!entry) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [entry, onClose]);

  useEffect(() => {
    setQuantity(1);
  }, [entry?.listingKey, selectedVariant?.id]);

  if (!entry || !selectedVariant) return null;

  const prompt =
    entry.groupBy === "color"
      ? "Choose a size before adding to cart"
      : entry.groupBy === "size"
        ? "Choose a color before adding to cart"
        : "Choose a variant before adding to cart";

  const maxQty = Math.max(0, Number(selectedVariant.stock) || 0);
  const canAdd = maxQty > 0;
  const safeQty = Math.min(Math.max(1, quantity), maxQty || 1);

  const decreaseQty = () => setQuantity((q) => Math.max(1, q - 1));
  const increaseQty = () => setQuantity((q) => Math.min(maxQty, q + 1));

  return (
    <div className="shop-variant-modal" role="presentation">
      <button
        type="button"
        className="shop-variant-modal__backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="shop-variant-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shop-variant-modal-title"
      >
        <header className="shop-variant-modal__head">
          <h2 id="shop-variant-modal-title" className="shop-variant-modal__title">
            {selectedVariant.name}
          </h2>
          <button
            type="button"
            className="shop-variant-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            <IoCloseOutline size={22} aria-hidden />
          </button>
        </header>

        <p className="shop-variant-modal__prompt">{prompt}</p>

        <ProductVariantPicker
          variants={entry.variants}
          product={selectedVariant}
          groupBy={entry.groupBy}
          onSelect={onSelect}
        />

        <div className="shop-variant-modal__price">
          <ProductPriceBlock product={selectedVariant} variant="shop" />
        </div>

        <div className="shop-variant-modal__quantity">
          <span className="shop-variant-modal__quantity-label">Quantity</span>
          <div className="shop-variant-modal__qty" role="group" aria-label="Quantity">
            <button
              type="button"
              className="shop-variant-modal__qty-btn"
              aria-label="Decrease quantity"
              onClick={decreaseQty}
              disabled={!canAdd || safeQty <= 1}
            >
              <IoRemoveOutline size={16} aria-hidden />
            </button>
            <span className="shop-variant-modal__qty-val">{safeQty}</span>
            <button
              type="button"
              className="shop-variant-modal__qty-btn"
              aria-label="Increase quantity"
              onClick={increaseQty}
              disabled={!canAdd || safeQty >= maxQty}
            >
              <IoAddOutline size={16} aria-hidden />
            </button>
          </div>
          {maxQty > 0 ? (
            <span className="shop-variant-modal__stock-hint">{maxQty} in stock</span>
          ) : null}
        </div>

        <div className="shop-variant-modal__actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={!canAdd}
            onClick={() => onConfirm(selectedVariant, safeQty)}
          >
            {canAdd ? (
              <BtnContent icon={IoCartOutline}>
                Add {safeQty} to cart
              </BtnContent>
            ) : (
              "Out of stock"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShopVariantModal;
